import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Music, ExternalLink, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistsPage = () => {
  const { t, language } = useLanguage();
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const tx = (es, en, pt) => isEs ? es : (isPt ? (pt || en) : en);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/artists`);
        setArtists(r.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen pt-24">
      <div className="container-custom py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#E8720C]/60 mb-3">OG MUSIC</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">Artistas</h1>
          <p className="text-white/40 max-w-xl mx-auto">Artistas que confian en OG MUSIC para llevar su musica al mundo. Descubre su trabajo y conecta con ellos.</p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-white/30">Cargando artistas...</div>
        ) : artists.length === 0 ? (
          <div className="text-center py-20">
            <Music className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-lg">Proximamente</p>
            <p className="text-white/20 text-sm mt-1">Pronto veras aqui a los artistas de OG MUSIC</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {artists.map(artist => (
              <div key={artist.id} className="group glass-card overflow-hidden border border-white/5 hover:border-[#E8720C]/20 transition-all"
                data-testid={`artist-card-${artist.id}`}>
                {/* Image */}
                <div className="relative h-56 bg-gradient-to-br from-[#E8720C]/10 to-black overflow-hidden">
                  {artist.image_url ? (
                    <img src={artist.image_url} alt={artist.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-5xl font-black text-white/10">{(artist.name || '?')[0]}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white">{artist.name}</h3>
                    {artist.genre && <p className="text-xs text-[#E8720C]">{artist.genre}</p>}
                  </div>
                </div>
                {/* Info */}
                <div className="p-5">
                  {artist.bio && <p className="text-sm text-white/50 mb-4 line-clamp-3">{artist.bio}</p>}
                  {/* Links */}
                  <div className="flex flex-wrap gap-2">
                    {artist.spotify_url && (
                      <a href={artist.spotify_url} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-colors">
                        <Play className="w-3 h-3" /> Spotify
                      </a>
                    )}
                    {artist.instagram_url && (
                      <a href={artist.instagram_url} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-pink-500/10 hover:bg-pink-500/20 text-pink-400 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-colors">
                        <ExternalLink className="w-3 h-3" /> Instagram
                      </a>
                    )}
                    {artist.youtube_url && (
                      <a href={artist.youtube_url} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-colors">
                        <ExternalLink className="w-3 h-3" /> YouTube
                      </a>
                    )}
                    {artist.website_url && (
                      <a href={artist.website_url} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/50 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-colors">
                        <ExternalLink className="w-3 h-3" /> Web
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistsPage;
